import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { HiArrowDown, HiArrowRight } from "react-icons/hi";
import { brand } from "../data/siteData";
import useInViewAnimation from "../hooks/useInViewAnimation";
import "./Hero.css";

gsap.registerPlugin(ScrollTrigger);

export default function Hero() {
  const sectionRef = useRef(null);
  const sunRef = useRef(null);
  const imageRef = useRef(null);
  const contentRef = useRef(null);
  const particlesRef = useInViewAnimation();

  // Mouse-follow sun (desktop only)
  useEffect(() => {
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (isTouch || reduceMotion || !sunRef.current) return undefined;

    const onMove = (e) => {
      const { innerWidth, innerHeight } = window;
      const x = (e.clientX / innerWidth - 0.5) * 40;
      const y = (e.clientY / innerHeight - 0.5) * 40;
      gsap.to(sunRef.current, {
        x,
        y,
        duration: 1.2,
        ease: "power3.out",
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // Scroll-driven exit animation
  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) return undefined;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 0.6,
        },
      });

      tl.to(contentRef.current, { y: -120, opacity: 0, ease: "none" }, 0)
        .to(sunRef.current, { y: -260, x: 140, scale: 0.7, ease: "none" }, 0)
        .to(imageRef.current, { scale: 1.15, ease: "none" }, 0);
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="hero" ref={sectionRef}>
      <div className="hero__image" ref={imageRef} />
      <div className="hero__sun" ref={sunRef} />
      <div className="hero__particles" ref={particlesRef} aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => (
          <span key={i} className={`hero__particle hero__particle--${i % 5}`} />
        ))}
      </div>
      <div className="hero__scrim" />

      <div className="container hero__content" ref={contentRef}>
        <span className="eyebrow hero__eyebrow">Follow The Sun</span>
        <h1 className="hero__title">
          {brand.name}
          <span className="hero__title-line glow-text">{brand.tagline}</span>
        </h1>
        <p className="hero__supporting">{brand.supporting}</p>
        <div className="hero__ctas">
          <a href="#energy" className="btn btn-primary">
            Explore Solar <HiArrowRight />
          </a>
          <Link to="/contact" className="btn btn-outline">
            Get A Quote
          </Link>
        </div>
      </div>

      <a href="#energy" className="hero__scroll-cue" aria-label="Scroll to next section">
        <HiArrowDown />
      </a>
    </section>
  );
}
